import { isNil } from 'lodash';
import { ModeOfOperation, padding, utils } from 'aes-js';
import { Buffer } from 'buffer';
import { getRandomBytes } from '@utils/randomisation';
import { StringEncoding } from '@models/string-encoding';

/**
 * @property data {string} - Ciphered data with base64 encoding
 * @property encodedIv {string} - Generated Initialisation Vector with base64 encoding
 */
export interface CipherResult {
    readonly cipher: string;
    readonly initializationVector: string;
};

export default class CipherService {
    /**
     *  Encrypts a string using AES 256 CBC with PKCS7 padding
     *  Note: PRNG is not securely random!
     * @param dataToEncrypt {string} - String data to encrypt
     * @param customKey {string} - Encryption key
     * @param ivLength {number} - Length of the initialisation vector
     */
    static encryptAes(dataToEncrypt: string, customKey: string, ivLength: number): CipherResult | null {
        try {
            return this.tryEncryptAes(dataToEncrypt, customKey, ivLength);
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    /**
     * Decrypts a Base 64 encoded encrypted string using AES 256 CBC with PKCS7 padding
     * @param encryptedData {string} - Encrypted data in Base 64 encoding
     * @param encodedIv {string} - Initialisation vector encoded with Base 64
     * @param customKey {string} - Custom key for decrypting data
     */
    static decryptAes(encryptedData: string, encodedIv: string, customKey: string): string | null {
        try {
            if (!encryptedData) {
                return null;
            }
            return this.tryDecryptAes(encryptedData, encodedIv, customKey);
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    private static tryEncryptAes(dataToEncrypt: string, key: string, ivLength: number): CipherResult {
        if (isNil(dataToEncrypt)) {
            throw new Error('Data to encrypt must be defined');
        }

        const keyBytes = Buffer.from(key, StringEncoding.BASE64);
        const dataBytes = Buffer.from(dataToEncrypt, StringEncoding.UTF8);
        const ivBytes = getRandomBytes(ivLength);

        const aesCbc = new ModeOfOperation.cbc(keyBytes, ivBytes);
        const encryptedBytes = aesCbc.encrypt(padding.pkcs7.pad(dataBytes));
        const encryptedData = Buffer.from(encryptedBytes).toString(StringEncoding.BASE64);

        return {
            cipher: encryptedData,
            initializationVector: ivBytes.toString(StringEncoding.BASE64)
        };
    }

    private static tryDecryptAes(encryptedData: string, encodedIv: string, customKey: string): string {
        const encodingType = 'base64';

        if (isNil(encryptedData)) {
            throw new Error('Encrypted data must be defined');
        }
        if (isNil(encodedIv)) {
            throw new Error('Encoded iv must be defined');
        }
        const keyBytes = Buffer.from(customKey, encodingType);
        const ivBytes = Buffer.from(encodedIv, encodingType);
        const cipherTextBytes = Buffer.from(encryptedData, encodingType);
        const aesCbc = new ModeOfOperation.cbc(keyBytes, ivBytes);
        const decryptedBytes = aesCbc.decrypt(cipherTextBytes);
        const decryptedData = utils.utf8.fromBytes(padding.pkcs7.strip(decryptedBytes));
        return decryptedData;
    }
}



// WEBPACK FOOTER //
// ./src/services/cipher-service.ts